import dayjs from 'dayjs';
import type { HybridPhenology } from './use-phenology';

/** How long the picker has to sit still before a typed date is simulated. */
export const SIMULATION_DEBOUNCE_MS = 400;

/**
 * - `pending`: the input moved and the simulation for it isn't on screen yet.
 * - `invalid`: the input holds no usable date.
 * - `failed`: the date was simulated but the phenology came back empty.
 * - `ready`: the simulation on screen belongs to the date in the input.
 */
export type SimulationStatus = 'pending' | 'invalid' | 'failed' | 'ready';

/**
 * A date the simulator can run. The year guard matches the picker's own default bounds and keeps a
 * half-typed year (`0002`) from reaching the API.
 */
export const isSimulatableDate = (date?: string): date is string => {
    if (!date || !dayjs(date).isValid()) return false;
    // Read off the string: `Date` parsing maps two-digit years to 19xx, so `0002` would pass as 1902.
    const year = Number(date.slice(0, 4));
    return year >= 1900 && year <= 2099;
};

/**
 * The phenology providers swallow request errors into empty entries, so a failed run only shows
 * up as hybrids without a single stage date.
 */
const hasPhenology = (hybrids: HybridPhenology[]) =>
    hybrids.some((hybrid) => hybrid.planting || hybrid.emergence || hybrid.flowering || hybrid.maturity);

/**
 * Where the open simulation stands, and whether "Aplicar fecha" may apply the input's date.
 *
 * `debouncedDate` lets a date that is still being typed read as `pending` rather than flashing the
 * invalid-date error on every keystroke; `requestedDate` is the date the query actually ran with.
 */
export const resolveSimulation = ({
    draftDate,
    debouncedDate,
    requestedDate,
    isLoading,
    hybrids,
    plantingDate,
}: {
    draftDate?: string;
    debouncedDate?: string;
    requestedDate?: string;
    isLoading: boolean;
    hybrids: HybridPhenology[];
    plantingDate?: string;
}): { status: SimulationStatus; canApply: boolean } => {
    if (!isSimulatableDate(draftDate)) {
        return { status: draftDate === debouncedDate ? 'invalid' : 'pending', canApply: false };
    }
    if (draftDate !== requestedDate || isLoading) return { status: 'pending', canApply: false };
    if (!hasPhenology(hybrids)) return { status: 'failed', canApply: false };
    // Applying wipes the chosen hybrids, so re-applying the stored date would only lose them.
    return { status: 'ready', canApply: draftDate !== plantingDate };
};
